import React, { useState, useEffect, useRef } from 'react';
import { PageHeader, Badge } from '../../components/UI';
import { adminApi } from '../../utils/api';
import toast from 'react-hot-toast';

const PRI_COLORS = { low: '#666', medium: '#ffd700', high: '#ff6b6b' };
const STATUS_TABS = ['all', 'open', 'in_progress', 'closed'];

export default function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTicket, setActiveTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [statusTab, setStatusTab] = useState('all');
  const [loadingThread, setLoadingThread] = useState(false);
  const bottomRef = useRef();

  useEffect(() => {
    loadTickets();
    const interval = setInterval(loadTickets, 30000); // auto-refresh every 30s
    return () => clearInterval(interval);
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [replies]);

  const loadTickets = async () => {
    try {
      const { data } = await adminApi.get('/api/admin/tickets');
      if (data.success) setTickets(data.tickets);
    } finally { setLoading(false); }
  };

  const openTicket = async (ticket) => {
    setActiveTicket(ticket);
    setLoadingThread(true);
    try {
      // Get replies using admin endpoint
      const { data } = await adminApi.get(`/api/admin/tickets/${ticket.id}/thread`);
      if (data.success) {
        setReplies(data.replies);
        // Mark as in_progress if open
        if (ticket.status === 'open') {
          await adminApi.put(`/api/admin/tickets/${ticket.id}`, { status: 'in_progress' });
          setTickets(ts => ts.map(t => t.id === ticket.id ? { ...t, status: 'in_progress' } : t));
          setActiveTicket(t => ({ ...t, status: 'in_progress' }));
        }
        // Mark admin replies read
        setTickets(ts => ts.map(t => t.id === ticket.id ? { ...t, unread_admin: 0 } : t));
      }
    } finally { setLoadingThread(false); }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      const { data } = await adminApi.put(`/api/admin/tickets/${activeTicket.id}`, {
        status: activeTicket.status === 'closed' ? 'closed' : 'in_progress',
        reply: replyText.trim()
      });
      if (data.success) {
        setReplies(prev => [...prev, { sender: 'admin', message: replyText.trim(), created_at: new Date().toISOString() }]);
        setReplyText('');
        toast.success('Reply sent');
      } else toast.error(data.message);
    } finally { setSending(false); }
  };

  const changeStatus = async (status) => {
    await adminApi.put(`/api/admin/tickets/${activeTicket.id}`, { status });
    setActiveTicket(t => ({ ...t, status }));
    setTickets(ts => ts.map(t => t.id === activeTicket.id ? { ...t, status } : t));
    toast.success('Status updated');
  };

  const formatTime = (d) => {
    const date = new Date(d);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };

  const filtered = statusTab === 'all' ? tickets : tickets.filter(t => t.status === statusTab);
  const openCount = tickets.filter(t => t.status === 'open').length;

  return (
    <div>
      <PageHeader title={<span>Support Tickets {openCount > 0 && <span style={{ background: '#ff6b6b', color: '#fff', padding: '2px 8px', borderRadius: 100, fontSize: 11, marginLeft: 8 }}>{openCount} new</span>}</span>} />

      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 20, height: 'calc(100vh - 200px)', minHeight: 560 }}>

        {/* ── LEFT: Ticket List ── */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Status filter tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', overflowX: 'auto' }}>
            {STATUS_TABS.map(tab => (
              <button key={tab} onClick={() => setStatusTab(tab)} style={{ flex: 1, padding: '10px 8px', background: 'none', border: 'none', color: statusTab === tab ? 'var(--green)' : 'rgba(255,255,255,0.3)', fontSize: 11, fontWeight: 600, cursor: 'pointer', borderBottom: statusTab === tab ? '2px solid var(--green)' : '2px solid transparent', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap', transition: 'all 0.15s' }}>
                {tab === 'all' ? `All (${tickets.length})` : tab === 'open' ? `Open (${tickets.filter(t=>t.status==='open').length})` : tab === 'in_progress' ? 'Active' : 'Closed'}
              </button>
            ))}
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.3)' }}>Loading...</div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>No tickets</div>
            ) : filtered.map(t => (
              <div key={t.id} onClick={() => openTicket(t)} style={{
                padding: '13px 14px', borderBottom: '1px solid var(--border)', cursor: 'pointer',
                background: activeTicket?.id === t.id ? 'rgba(0,231,76,0.06)' : t.status === 'open' ? 'rgba(255,215,0,0.02)' : 'transparent',
                borderLeft: activeTicket?.id === t.id ? '3px solid var(--green)' : t.status === 'open' ? '3px solid #ffd700' : '3px solid transparent',
                transition: 'all 0.15s'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#fff', flex: 1, marginRight: 8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.subject}</div>
                  <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', flexShrink: 0 }}>{formatTime(t.updated_at || t.created_at)}</span>
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 6 }}>{t.user_name} · {t.email}</div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <Badge>{t.status}</Badge>
                  <span style={{ background: `${PRI_COLORS[t.priority]}22`, color: PRI_COLORS[t.priority], padding: '2px 6px', borderRadius: 100, fontSize: 10, fontWeight: 700 }}>{t.priority?.toUpperCase()}</span>
                  <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', marginLeft: 'auto', fontFamily: 'monospace' }}>#{t.ticket_number}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── RIGHT: Chat Thread ── */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {!activeTicket ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.2)' }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>🎫</div>
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Select a ticket</div>
              <div style={{ fontSize: 13 }}>Click a ticket on the left to view the conversation</div>
            </div>
          ) : (
            <>
              {/* Thread Header */}
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', background: 'rgba(255,255,255,0.02)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#fff', marginBottom: 6 }}>{activeTicket.subject}</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>#{activeTicket.ticket_number}</span>
                      <Badge>{activeTicket.status}</Badge>
                      <span style={{ background: `${PRI_COLORS[activeTicket.priority]}22`, color: PRI_COLORS[activeTicket.priority], padding: '2px 7px', borderRadius: 100, fontSize: 10, fontWeight: 700 }}>{activeTicket.priority?.toUpperCase()}</span>
                      <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>by {activeTicket.user_name} ({activeTicket.email})</span>
                    </div>
                  </div>
                  {/* Status changer */}
                  <select value={activeTicket.status} onChange={e => changeStatus(e.target.value)} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '7px 12px', color: '#fff', fontSize: 12, cursor: 'pointer' }}>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>

              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
                {loadingThread ? (
                  <div style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.3)' }}>Loading...</div>
                ) : replies.map((r, i) => {
                  const isAdmin = r.sender === 'admin';
                  return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: isAdmin ? 'flex-end' : 'flex-start' }}>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginBottom: 4, paddingLeft: isAdmin ? 0 : 12, paddingRight: isAdmin ? 12 : 0 }}>
                        {isAdmin ? '🛡 You (Admin)' : `👤 ${activeTicket.user_name}`} · {formatTime(r.created_at)}
                      </div>
                      <div style={{
                        maxWidth: '72%', padding: '12px 16px',
                        borderRadius: isAdmin ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
                        background: isAdmin ? 'rgba(255,215,0,0.12)' : 'rgba(255,255,255,0.07)',
                        border: isAdmin ? '1px solid rgba(255,215,0,0.2)' : '1px solid rgba(255,255,255,0.06)',
                        color: '#fff', fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                      }}>
                        {r.message}
                      </div>
                    </div>
                  );
                })}
                {activeTicket.status === 'closed' && (
                  <div style={{ textAlign: 'center', padding: 12, background: 'rgba(255,255,255,0.04)', borderRadius: 8, fontSize: 13, color: 'rgba(255,255,255,0.3)' }}>
                    🔒 Ticket closed
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              {/* Reply Input */}
              <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 12, alignItems: 'flex-end', background: 'rgba(255,255,255,0.01)' }}>
                <textarea
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                  placeholder="Reply to client... (Enter to send)"
                  style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '12px 16px', color: '#fff', fontSize: 14, resize: 'none', minHeight: 48, maxHeight: 120, lineHeight: 1.5, outline: 'none', fontFamily: 'DM Sans' }}
                />
                <button onClick={sendReply} disabled={sending || !replyText.trim()} style={{ background: replyText.trim() ? '#ffd700' : 'rgba(255,255,255,0.06)', color: replyText.trim() ? '#000' : 'rgba(255,255,255,0.2)', border: 'none', width: 48, height: 48, borderRadius: 12, cursor: replyText.trim() ? 'pointer' : 'default', fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s' }}>
                  {sending ? '⏳' : '➤'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}